import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["slider", "item"];
  static values = {
    autoPlay: { type: Boolean, default: false },
    interval: { type: Number, default: 3000 },
    resumeDelay: { type: Number, default: 5000 }
  }

  connect() {
    this.currentIndex = 0;

    if (this.autoPlayValue) {
      this.startObserver();
      this.bindUserInteraction();
    }
  }

  // -------------------
  // Intersection Observer
  // -------------------
  startObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }
      });
    }, { threshold: 0.5 });

    this.observer.observe(this.element);
  }

  // -------------------
  // Autoplay
  // -------------------
  startAutoplay() {
    if (this.timer) return;
    this.timer = setInterval(() => this.autoplayNext(), this.intervalValue);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  pauseAndResume() {
    this.stopAutoplay();

    if (this.resumeTimer) {
      clearTimeout(this.resumeTimer);
    }

    this.resumeTimer = setTimeout(() => {
      this.startAutoplay();
    }, this.resumeDelayValue);
  }

  // -------------------
  // Navigation
  // -------------------
  autoplayNext() {
    this.currentIndex = (this.currentIndex + 1) % this.itemTargets.length;
    this.scrollToIndex(this.currentIndex);
  }

  next() {
    this.currentIndex = (this.currentIndex + 1) % this.itemTargets.length;
    this.scrollToIndex(this.currentIndex);

    if (this.autoPlayValue) {
      this.pauseAndResume();
    }
  }

  previous() {
    this.currentIndex =
      (this.currentIndex - 1 + this.itemTargets.length) %
      this.itemTargets.length;
    this.scrollToIndex(this.currentIndex);

    if (this.autoPlayValue) {
      this.pauseAndResume();
    }
  }

  scrollToIndex(index) {
    const offset = this.itemTargets[index].offsetLeft;
    this.sliderTarget.scrollTo({ left: offset, behavior: "smooth" });
  }

  // -------------------
  // User interaction binding
  // -------------------
  bindUserInteraction() {
    if (!this.autoPlayValue) return;

    this._boundPauseAndResume = this.pauseAndResume.bind(this);

    this.sliderTarget.addEventListener("touchstart", this._boundPauseAndResume, { passive: true });
    this.sliderTarget.addEventListener("mousedown", this._boundPauseAndResume);
    this.sliderTarget.addEventListener("wheel", this._boundPauseAndResume, { passive: true });
  }

  unbindUserInteraction() {
    if (!this._boundPauseAndResume) return;

    this.sliderTarget.removeEventListener("touchstart", this._boundPauseAndResume);
    this.sliderTarget.removeEventListener("mousedown", this._boundPauseAndResume);
    this.sliderTarget.removeEventListener("wheel", this._boundPauseAndResume);

    this._boundPauseAndResume = null;
  }

  // -------------------
  // Cleanup
  // -------------------
  disconnect() {
    this.stopAutoplay();
    if (this.observer) this.observer.disconnect();
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.unbindUserInteraction();
  }
}